import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BrainCircuit, AudioLines, PenTool, Sparkles, Mic2, Mail,
  Presentation, FileText, Heart, Zap, BookOpen, Laugh,
} from "lucide-react";

type Stage = { label: string; icon: React.ElementType; duration: number };

const DEFAULT_STAGES: Stage[] = [
  { label: "Reading your voice DNA...", icon: BrainCircuit, duration: 1800 },
  { label: "Mapping your tone & cadence...", icon: AudioLines, duration: 2000 },
  { label: "Drafting the structure...", icon: PenTool, duration: 2200 },
  { label: "Polishing to sound like you...", icon: Sparkles, duration: 2800 },
];

const FORMAT_STAGES: Record<string, Stage[]> = {
  "Comedy set": [
    { label: "Reading your comedic timing...", icon: Laugh, duration: 1800 },
    { label: "Finding the setup & misdirect...", icon: BrainCircuit, duration: 2000 },
    { label: "Writing the punchlines...", icon: PenTool, duration: 2200 },
    { label: "Polishing the delivery...", icon: Sparkles, duration: 2800 },
  ],
  "Newsletter": [
    { label: "Reading your voice DNA...", icon: BrainCircuit, duration: 1800 },
    { label: "Crafting the opening hook...", icon: Mail, duration: 2000 },
    { label: "Developing the narrative arc...", icon: PenTool, duration: 2200 },
    { label: "Refining the closing...", icon: Sparkles, duration: 2800 },
  ],
  "Keynote opener": [
    { label: "Reading your presence & tone...", icon: BrainCircuit, duration: 1800 },
    { label: "Crafting the opening moment...", icon: Presentation, duration: 2000 },
    { label: "Building the story arc...", icon: PenTool, duration: 2200 },
    { label: "Making it land...", icon: Sparkles, duration: 2800 },
  ],
  "Podcast intro": [
    { label: "Capturing your spoken energy...", icon: Mic2, duration: 1800 },
    { label: "Setting the episode context...", icon: AudioLines, duration: 2000 },
    { label: "Writing the hook...", icon: PenTool, duration: 2200 },
    { label: "Polishing the delivery...", icon: Sparkles, duration: 2800 },
  ],
  "Cover letter": [
    { label: "Reading your voice DNA...", icon: BrainCircuit, duration: 1800 },
    { label: "Framing your value clearly...", icon: FileText, duration: 2000 },
    { label: "Tailoring the narrative...", icon: PenTool, duration: 2200 },
    { label: "Making it memorable...", icon: Sparkles, duration: 2800 },
  ],
  "Cover Letter Generator": [
    { label: "Reading your voice DNA...", icon: BrainCircuit, duration: 1800 },
    { label: "Framing your value clearly...", icon: FileText, duration: 2000 },
    { label: "Tailoring the narrative...", icon: PenTool, duration: 2200 },
    { label: "Making it memorable...", icon: Sparkles, duration: 2800 },
  ],
  "Cold email": [
    { label: "Reading your voice DNA...", icon: BrainCircuit, duration: 1800 },
    { label: "Crafting the subject hook...", icon: Mail, duration: 2000 },
    { label: "Writing the ask...", icon: PenTool, duration: 2200 },
    { label: "Making it irresistible...", icon: Sparkles, duration: 2800 },
  ],
  "Wedding toast": [
    { label: "Reading your voice & warmth...", icon: Heart, duration: 1800 },
    { label: "Finding the perfect story...", icon: BrainCircuit, duration: 2000 },
    { label: "Writing from the heart...", icon: PenTool, duration: 2200 },
    { label: "Polishing the emotional landing...", icon: Sparkles, duration: 2800 },
  ],
  "60-sec pitch": [
    { label: "Reading your voice DNA...", icon: BrainCircuit, duration: 1800 },
    { label: "Sharpening the hook...", icon: Zap, duration: 2000 },
    { label: "Crafting the value arc...", icon: PenTool, duration: 2200 },
    { label: "Making every word count...", icon: Sparkles, duration: 2800 },
  ],
  "Short story": [
    { label: "Reading your narrative voice...", icon: BookOpen, duration: 1800 },
    { label: "Setting the scene...", icon: BrainCircuit, duration: 2000 },
    { label: "Building the arc...", icon: PenTool, duration: 2200 },
    { label: "Polishing the ending...", icon: Sparkles, duration: 2800 },
  ],
};

interface StudioProgressProps {
  isActive: boolean;
  format: string | null;
}

export function StudioProgress({ isActive, format }: StudioProgressProps) {
  const [stageIndex, setStageIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  const stages = (format && FORMAT_STAGES[format]) ?? DEFAULT_STAGES;

  useEffect(() => {
    if (!isActive) {
      setStageIndex(0);
      setProgress(0);
      return;
    }

    setStageIndex(0);
    setProgress(0);

    let currentStage = 0;
    const timers: ReturnType<typeof setTimeout>[] = [];

    const advanceStage = () => {
      if (currentStage < stages.length - 1) {
        currentStage++;
        setStageIndex(currentStage);
      }
    };

    let elapsed = 0;
    for (let i = 0; i < stages.length - 1; i++) {
      elapsed += stages[i].duration;
      timers.push(setTimeout(advanceStage, elapsed));
    }

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 92) return prev;
        const increment = prev < 30 ? 2.5 : prev < 60 ? 1.5 : prev < 80 ? 0.8 : 0.3;
        return Math.min(prev + increment, 92);
      });
    }, 200);

    return () => {
      timers.forEach(clearTimeout);
      clearInterval(progressInterval);
    };
  }, [isActive, format]);

  if (!isActive) return null;

  const stage = stages[stageIndex];
  const StageIcon = stage.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col items-center justify-center gap-5 py-7"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="relative w-14 h-14">
        <motion.div
          className="absolute inset-0 rounded-2xl"
          style={{ background: "radial-gradient(circle, hsl(188 90% 42% / 0.3), transparent 70%)" }}
          animate={{ scale: [1, 1.25, 1], opacity: [0.6, 0.15, 0.6] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute inset-0 rounded-2xl"
          style={{ background: "linear-gradient(135deg, hsl(188 90% 42% / 0.18), hsl(205 88% 52% / 0.12))" }}
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="absolute inset-0 rounded-2xl flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={stageIndex}
              initial={{ opacity: 0, scale: 0.7, y: 6 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.7, y: -6 }}
              transition={{ duration: 0.28 }}
            >
              <StageIcon className="w-6 h-6 text-primary" style={{ filter: "drop-shadow(0 0 8px hsl(188 90% 42% / 0.8))" }} />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <div className="w-full space-y-2.5">
        <div
          className="h-1.5 bg-secondary/50 rounded-full overflow-hidden"
          role="progressbar"
          aria-valuenow={Math.round(progress)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Studio generation progress"
        >
          <div className="relative h-full">
            <motion.div
              className="absolute inset-y-0 left-0 rounded-full"
              style={{ background: "linear-gradient(90deg, hsl(188 90% 42%), hsl(205 88% 52%))" }}
              initial={{ width: "0%" }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            />
            <motion.div
              className="absolute inset-y-0 right-0 w-8 rounded-full pointer-events-none"
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              style={{
                background: "transparent",
                boxShadow: "4px 0 16px 4px hsl(205 88% 52% / 0.7)",
                left: 0,
              }}
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.p
            key={stageIndex}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.22 }}
            className="text-xs font-medium text-muted-foreground text-center"
          >
            {stage.label}
          </motion.p>
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
